import { prisma } from "@/lib/db";
import {
  FOCUS_SET_SIZE,
  GRADUATION_THRESHOLD,
  GRADUATION_KEEP_SHAKY,
  PHASE_1_TOTAL_PROBLEMS,
  BOX_GRADUATED,
  LEITNER_INTERVALS,
  DECAY_LAMBDA,
  NEW_ITEM_P_CORRECT,
} from "./constants";
import type {
  Phase,
  Phase1BoxStatus,
  Phase2BoxStatus,
  BoxStatus,
  GraduationResult,
  PhaseTransitionResult,
} from "./types";

// ============================================================================
// p_correct Calculation (kept for compatibility)
// ============================================================================

/**
 * Calculate p_correct using exponential decay over last 10 attempts
 */
export async function calculatePCorrect(
  userId: string,
  problemId: string,
  now: Date
): Promise<number> {
  const logs = await prisma.reviewLog.findMany({
    where: { userId, problemId },
    orderBy: { timestamp: "desc" },
    take: 10,
  });

  if (logs.length === 0) {
    return NEW_ITEM_P_CORRECT;
  }

  let weightedSum = 0;
  let totalWeight = 0;

  for (const log of logs) {
    const hoursSince = (now.getTime() - log.timestamp.getTime()) / (1000 * 60 * 60);
    const weight = Math.exp(-DECAY_LAMBDA * hoursSince);
    weightedSum += weight * (log.wasCorrect ? 1 : 0);
    totalWeight += weight;
  }

  if (totalWeight === 0) {
    return NEW_ITEM_P_CORRECT;
  }

  return weightedSum / totalWeight;
}

/**
 * Synchronous p_correct calculation when we already have the logs
 */
export function calculatePCorrectFromLogs(
  logs: Array<{ timestamp: Date; wasCorrect: boolean }>,
  now: Date
): number {
  if (logs.length === 0) {
    return NEW_ITEM_P_CORRECT;
  }

  let weightedSum = 0;
  let totalWeight = 0;

  for (const log of logs) {
    const hoursSince = (now.getTime() - log.timestamp.getTime()) / (1000 * 60 * 60);
    const weight = Math.exp(-DECAY_LAMBDA * hoursSince);
    weightedSum += weight * (log.wasCorrect ? 1 : 0);
    totalWeight += weight;
  }

  if (totalWeight === 0) {
    return NEW_ITEM_P_CORRECT;
  }

  return weightedSum / totalWeight;
}

// ============================================================================
// User Phase Management
// ============================================================================

/**
 * Get or create user phase record
 */
export async function getUserPhase(userId: string): Promise<{ currentPhase: Phase; totalSeen: number }> {
  let userPhase = await prisma.userPhase.findUnique({
    where: { userId },
  });

  if (!userPhase) {
    userPhase = await prisma.userPhase.create({
      data: { userId, currentPhase: 1, totalSeen: 0 },
    });
  }

  return {
    currentPhase: userPhase.currentPhase as Phase,
    totalSeen: userPhase.totalSeen,
  };
}

/**
 * Increment totalSeen count
 */
export async function incrementTotalSeen(userId: string, count: number): Promise<void> {
  await prisma.userPhase.update({
    where: { userId },
    data: { totalSeen: { increment: count } },
  });
}

// ============================================================================
// Box Status
// ============================================================================

/**
 * Get box distribution for Phase 1
 */
export async function getPhase1BoxStatus(userId: string): Promise<Phase1BoxStatus> {
  const states = await prisma.userProblemState.findMany({
    where: { userId },
    select: { box: true },
  });

  const totalProblems = await prisma.problem.count();
  const statesCount = states.length;

  let box0 = 0, box1 = 0, box2 = 0, box3 = 0, graduated = 0;

  for (const s of states) {
    switch (s.box) {
      case 0: box0++; break;
      case 1: box1++; break;
      case 2: box2++; break;
      case 3: box3++; break;
      case BOX_GRADUATED: graduated++; break;
    }
  }

  return {
    box0,
    box1,
    box2,
    box3,
    graduated,
    totalUnseen: totalProblems - statesCount,
  };
}

/**
 * Get box distribution for Phase 2
 */
export async function getPhase2BoxStatus(userId: string): Promise<Phase2BoxStatus> {
  const now = new Date();

  const states = await prisma.userProblemState.findMany({
    where: { userId },
    select: { box: true, dueAt: true },
  });

  let box1 = 0, box2 = 0, box3 = 0, box4 = 0, box5 = 0, dueNow = 0;

  for (const s of states) {
    switch (s.box) {
      case 1: box1++; break;
      case 2: box2++; break;
      case 3: box3++; break;
      case 4: box4++; break;
      case 5: box5++; break;
    }

    if (s.dueAt && s.dueAt <= now) {
      dueNow++;
    }
  }

  return { box1, box2, box3, box4, box5, dueNow };
}

/**
 * Get combined box status
 */
export async function getBoxStatus(userId: string): Promise<BoxStatus> {
  const { currentPhase, totalSeen } = await getUserPhase(userId);
  const totalProblems = await prisma.problem.count();

  if (currentPhase === 1) {
    const phase1 = await getPhase1BoxStatus(userId);
    return {
      phase: 1,
      totalSeen,
      totalProblems,
      phase1,
    };
  } else {
    const phase2 = await getPhase2BoxStatus(userId);
    return {
      phase: 2,
      totalSeen,
      totalProblems,
      phase2,
    };
  }
}

// ============================================================================
// Focus Set Initialization (Phase 1)
// ============================================================================

/**
 * Initialize focus set for a new user (grab first FOCUS_SET_SIZE problems)
 */
export async function initializeFocusSet(userId: string): Promise<number> {
  // Ensure user phase record exists
  await getUserPhase(userId);

  // Check if user already has problems in focus (boxes 0-3)
  const existingFocus = await prisma.userProblemState.count({
    where: {
      userId,
      box: { in: [0, 1, 2, 3] },
    },
  });

  if (existingFocus > 0) {
    return existingFocus;
  }

  // Get first FOCUS_SET_SIZE problems that don't have a state
  const problems = await prisma.problem.findMany({
    where: {
      userProblemStates: {
        none: { userId },
      },
    },
    orderBy: [
      { groupId: "asc" },
      { sequenceInGroup: "asc" },
    ],
    take: FOCUS_SET_SIZE,
  });

  if (problems.length > 0) {
    await prisma.userProblemState.createMany({
      data: problems.map(p => ({
        userId,
        problemId: p.id,
        box: 0,
        set: "focus",  // Legacy compatibility
      })),
    });

    // Increment totalSeen
    await incrementTotalSeen(userId, problems.length);
  }

  return problems.length;
}

// ============================================================================
// Graduation (Phase 1)
// ============================================================================

/**
 * Check and perform graduation when enough cards reach Box 3
 *
 * When 10 cards reach Box 3 (Stable):
 * 1. Retire those 10 cards (mark as graduated, box = 99)
 * 2. Keep 2 shaky cards (lowest box/correctCount)
 * 3. Add next 10 unseen cards chronologically (box = 0)
 */
export async function checkAndPerformGraduation(userId: string): Promise<GraduationResult> {
  // Get all cards in Box 3
  const stableCards = await prisma.userProblemState.findMany({
    where: { userId, box: 3 },
    orderBy: { correctCount: "desc" },
  });

  if (stableCards.length < GRADUATION_THRESHOLD) {
    return {
      graduated: false,
      graduatedCount: 0,
      newCardsAdded: 0,
      shakyCardsKept: 0,
    };
  }

  // Graduate the top GRADUATION_THRESHOLD cards
  const toGraduate = stableCards.slice(0, GRADUATION_THRESHOLD);
  const graduateIds = toGraduate.map(c => c.problemId);

  await prisma.userProblemState.updateMany({
    where: {
      userId,
      problemId: { in: graduateIds },
    },
    data: {
      box: BOX_GRADUATED,
      set: "old",  // Legacy compatibility
    },
  });

  // Get shaky cards to keep (lowest box first, then lowest correctCount)
  const shakyCards = await prisma.userProblemState.findMany({
    where: {
      userId,
      box: { in: [0, 1, 2] },
    },
    orderBy: [
      { box: "asc" },
      { correctCount: "asc" },
    ],
    take: GRADUATION_KEEP_SHAKY,
  });

  // Add new unseen cards
  const newCards = await prisma.problem.findMany({
    where: {
      userProblemStates: {
        none: { userId },
      },
    },
    orderBy: [
      { groupId: "asc" },
      { sequenceInGroup: "asc" },
    ],
    take: GRADUATION_THRESHOLD,
  });

  if (newCards.length > 0) {
    await prisma.userProblemState.createMany({
      data: newCards.map(p => ({
        userId,
        problemId: p.id,
        box: 0,
        set: "focus",  // Legacy compatibility
      })),
    });

    // Increment totalSeen
    await incrementTotalSeen(userId, newCards.length);
  }

  return {
    graduated: true,
    graduatedCount: toGraduate.length,
    newCardsAdded: newCards.length,
    shakyCardsKept: shakyCards.length,
  };
}

// ============================================================================
// Phase Transition
// ============================================================================

/**
 * Check and perform transition to Phase 2
 *
 * When totalSeen >= 150:
 * 1. Map Phase 1 boxes to Phase 2: 0-1->1, 2->2, 3->3, 99->4
 * 2. Set initial due dates based on box intervals
 * 3. Update userPhase.currentPhase = 2
 */
export async function checkAndPerformPhaseTransition(userId: string): Promise<PhaseTransitionResult> {
  const { currentPhase, totalSeen } = await getUserPhase(userId);

  // Already in Phase 2
  if (currentPhase === 2) {
    return { transitioned: false, newPhase: 2 };
  }

  // Not enough problems seen yet
  if (totalSeen < PHASE_1_TOTAL_PROBLEMS) {
    return { transitioned: false, newPhase: 1 };
  }

  const now = new Date();

  // Get all user problem states
  const states = await prisma.userProblemState.findMany({
    where: { userId },
  });

  // Map boxes and set due dates
  for (const state of states) {
    let newBox: number;
    let dueAt: Date;

    // Map Phase 1 boxes to Phase 2
    if (state.box === 0 || state.box === 1) {
      newBox = 1;
    } else if (state.box === 2) {
      newBox = 2;
    } else if (state.box === 3) {
      newBox = 3;
    } else if (state.box === BOX_GRADUATED) {
      newBox = 4;
    } else {
      newBox = 1;
    }

    // Set initial due date based on new box
    const intervalDays = LEITNER_INTERVALS[newBox] || 1;
    dueAt = new Date(now.getTime() + intervalDays * 24 * 60 * 60 * 1000);

    await prisma.userProblemState.update({
      where: {
        userId_problemId: { userId, problemId: state.problemId },
      },
      data: {
        box: newBox,
        dueAt,
      },
    });
  }

  // Update user phase
  await prisma.userPhase.update({
    where: { userId },
    data: { currentPhase: 2 },
  });

  return { transitioned: true, newPhase: 2 };
}

// ============================================================================
// Legacy Compatibility
// ============================================================================

/**
 * Check if Focus set has reached mastery (legacy - now uses graduation)
 */
export async function checkFocusMastery(userId: string): Promise<{
  mastered: boolean;
  correctCount: number;
  totalCount: number;
}> {
  // In the new system, "mastery" is replaced by graduation
  // Check if we have enough cards in Box 3 to graduate
  const stableCount = await prisma.userProblemState.count({
    where: { userId, box: 3 },
  });

  const focusCount = await prisma.userProblemState.count({
    where: {
      userId,
      box: { in: [0, 1, 2, 3] },
    },
  });

  return {
    mastered: stableCount >= GRADUATION_THRESHOLD,
    correctCount: stableCount,
    totalCount: focusCount,
  };
}

/**
 * Promote focus set (legacy - now uses graduation)
 */
export async function promoteFocusSet(userId: string): Promise<number> {
  const result = await checkAndPerformGraduation(userId);
  return result.newCardsAdded;
}

/**
 * Get counts for each set (legacy compatibility)
 */
export async function getSetCounts(userId: string): Promise<{
  newCount: number;
  focusCount: number;
  oldCount: number;
  focusCorrectCount: number;
}> {
  const totalProblems = await prisma.problem.count();

  const [focusStates, oldCount] = await Promise.all([
    prisma.userProblemState.findMany({
      where: {
        userId,
        box: { in: [0, 1, 2, 3] },
      },
    }),
    prisma.userProblemState.count({
      where: {
        userId,
        box: BOX_GRADUATED,
      },
    }),
  ]);

  const focusCount = focusStates.length;
  // In new system, "correct" means Box 3 (stable)
  const focusCorrectCount = focusStates.filter(s => s.box === 3).length;
  const newCount = totalProblems - focusCount - oldCount;

  return { newCount, focusCount, oldCount, focusCorrectCount };
}
